import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useCallback, useMemo, useState } from 'react';
import {
    faAngleDown,
    faAngleUp,
    faCheck,
    faDownload,
    faFileCircleExclamation,
    faSpinner,
    faUpRightFromSquare,
    faXmark,
} from '@fortawesome/free-solid-svg-icons';
import MiddleEllipsis from 'react-middle-ellipsis';
import { ReportStatus } from '@/graphql/types';
import { DownloadStatusTranslations } from '../pages/downloads/enums';
import { IconButton } from '../form/Button';

const iconStyle = 'h-6 w-6';

interface DownloadFile {
    id: string;
    name: string;
    status: ReportStatus;
    url: string;
}

type UpdateLastDownloadMutation<T = unknown> = (options: {
    variables: { id: string };
}) => Promise<T> | T;

const STATUS_ICON: Record<
    ReportStatus,
    {
        icon: IconProp;
        className: string;
    }
> = {
    [ReportStatus.Ready]: {
        icon: faDownload,
        className: iconStyle,
    },
    [ReportStatus.Failed]: {
        icon: faFileCircleExclamation,
        className: `${iconStyle} text-red-600`,
    },
    [ReportStatus.InProgress]: {
        icon: faSpinner,
        className: `${iconStyle} animate-spin`,
    },
    [ReportStatus.Downloaded]: {
        icon: faCheck,
        className: `${iconStyle} text-green-600`,
    },
};

export interface StatusPopupProps {
    files: DownloadFile[];
    onClose: () => void;
    updateLastDownloadMutation: UpdateLastDownloadMutation;
}

function StatusPopup({
    files,
    onClose,
    updateLastDownloadMutation,
}: StatusPopupProps): JSX.Element {
    const [showFiles, setShowFiles] = useState(true);
    const downloadsReady = useMemo(
        () => files.filter((file) => file.status === ReportStatus.Ready),
        [files]
    );

    const downloadFile = useCallback(
        (url: string, id: string) => {
            window.open(url, '_self');
            updateLastDownloadMutation({
                variables: {
                    id,
                },
            });
        },
        [updateLastDownloadMutation]
    );

    const showStatusIcon = useCallback(
        (status: ReportStatus, url: string, id: string) => {
            const { icon, className } = STATUS_ICON[status];
            const onClick = status === ReportStatus.Ready ? () => downloadFile(url, id) : undefined;
            return (
                <IconButton
                    ariaLabel={DownloadStatusTranslations[status]}
                    onClick={onClick}
                    icon={icon}
                    className={className}
                />
            );
        },
        [downloadFile]
    );

    const popupHeaderButtons = useMemo(
        () => [
            {
                ariaLabel: showFiles ? gettext('HIDE') : gettext('SHOW'),
                onClick: () => {
                    setShowFiles((current) => !current);
                },
                icon: showFiles ? faAngleDown : faAngleUp,
                className: `${iconStyle} px-1.5`,
            },
            {
                ariaLabel: gettext('CLOSE'),
                onClick: () => {
                    onClose();
                },
                icon: faXmark,
                className: iconStyle,
            },
        ],
        [onClose, showFiles]
    );

    return (
        <div className="w-90 shadow-448xl fixed bottom-0 left-4 z-50 min-w-80 text-xs font-semibold">
            <div className="bg-hc-dark-grey flex h-12 items-center justify-between rounded-t-md px-3 text-white">
                {`${gettext('DOWNLOAD_PAGE_TITLE').toUpperCase()} (${downloadsReady.length}/${
                    files.length
                })`}
                <div className="flex">
                    <a href="/reports/downloads" aria-label={gettext('GO_TO_PAGE')}>
                        <IconButton
                            ariaLabel={gettext('GO_TO_PAGE')}
                            icon={faUpRightFromSquare}
                            className={`${iconStyle} text-white`}
                        />
                    </a>
                    {popupHeaderButtons.map(({ ariaLabel, onClick, icon, className }) => (
                        <IconButton
                            ariaLabel={ariaLabel}
                            onClick={onClick}
                            icon={icon}
                            className={className}
                            key={ariaLabel}
                        />
                    ))}
                </div>
            </div>
            <div className="max-h-36 overflow-y-scroll overscroll-contain">
                {showFiles &&
                    files.map(({ name, id, status, url }) => (
                        <div
                            className="border-t-0.5 border-hc-dark-grey flex h-12 items-center justify-between bg-white px-5"
                            key={id}
                        >
                            <div className="w-60 whitespace-nowrap" title={name}>
                                <MiddleEllipsis>
                                    <span>{name}</span>
                                </MiddleEllipsis>
                            </div>
                            {showStatusIcon(status, url, id)}
                        </div>
                    ))}
            </div>
        </div>
    );
}

export default StatusPopup;
